import { ReactNode, Suspense } from 'react'

import { clsx } from 'clsx'
import { generatePath, useParams } from 'react-router'
import { Link, matchPath } from 'react-router-dom'

import { isSupportEmail } from '@bettermode/common/feature-flags'
import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { useRouterPath } from '@tribeplatform/react-sdk'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { SectionHeader } from '@tribeplatform/react-ui-kit/SectionHeader'
import { NavVertical } from '@tribeplatform/react-ui-kit/Sidebar'

import { useCustomizer } from '../Editor/hooks/index.js'
import { CustomizerActionType } from '../Editor/types.js'

import { SidebarBackButton } from './SidebarBackButton.js'
import { SidebarItem } from './sidebars.js'

export const findDeepestMatchingRoute = (
  item: SidebarItem,
  pathname: string,
): SidebarItem | null => {
  const matching = item.children?.find(item => {
    return matchPath(
      {
        path: item.path,
        end: item.exact ?? false,
        caseSensitive: item.exact ?? false,
      },
      pathname,
    )
  })
  if (matching?.noNest && matching?.path === pathname) {
    return item
  }
  if (matching?.children) {
    return findDeepestMatchingRoute(matching, pathname)
  }
  if (matching) {
    return matching
  }
  return item
}

interface Props {
  sidebar: SidebarItem
  useProvidedSidebar?: boolean
}

export const NestedSidebarContent = ({
  sidebar,
  useProvidedSidebar = false,
}: Props) => {
  const { $t } = useI18n()
  const pathname = useRouterPath()
  const urlParams = useParams()
  const {
    data: { member },
  } = useAuthToken()
  const {
    state: { activeBlock, blockSettings },
    dispatch,
  } = useCustomizer()
  const activeItem = useProvidedSidebar
    ? sidebar
    : findDeepestMatchingRoute(sidebar, pathname)

  const decisionSpaceTemplateManagement = isSupportEmail(member?.email ?? '')

  if (!activeItem) {
    return null
  }
  const popSettings = blockSettings.length > 1

  let title: ReactNode | string
  let description: ReactNode | string
  if (activeBlock) {
    if (activeBlock.action === 'edit') {
      title = blockSettings[blockSettings.length - 1].title
      description = blockSettings[blockSettings.length - 1].description
    } else {
      title = $t({
        defaultMessage: 'Add block',
        id: 'Admin.Customizer.AddBlock',
      })
    }
  } else {
    title = activeItem.title
    description = activeItem.description
  }

  if (activeItem.fullPage && activeItem.component) {
    return activeItem.component
  }

  return (
    <NavVertical className="flex-grow">
      <NavVertical.Group className="flex-grow">
        {!activeBlock?.fullSidebarSettings && (
          <div className="space-y-4">
            {activeItem.backTo !== false && (
              <SidebarBackButton
                onClick={e => {
                  if (activeBlock) {
                    e.preventDefault()
                    e.stopPropagation()

                    if (popSettings) {
                      dispatch({
                        type: CustomizerActionType.POP_BLOCK_SETTINGS,
                      })
                    } else {
                      dispatch({
                        type: CustomizerActionType.DEACTIVATE_BLOCK,
                      })
                    }
                  }
                }}
                href={activeItem.backTo || adminRoutes.design.customizer.root}
              />
            )}
            <div className={clsx(!activeItem.noPaddingX && 'px-3')}>
              <SectionHeader title={title} description={description} />
            </div>
          </div>
        )}
        <Suspense fallback={<></>}>
          <div
            className={clsx(
              'flex flex-col space-y-1',
              !activeItem.noPaddingX && 'px-3',
              !activeItem.noPaddingY && 'py-3',
            )}
          >
            {activeItem.component
              ? activeItem.component
              : activeItem?.children?.map(item => {
                  if (item.hidden) {
                    return null
                  }

                  const active = matchPath(
                    {
                      path: item.path,
                      caseSensitive: true,
                      end: false,
                    },
                    pathname,
                  )

                  if (
                    item.featureFlag === 'create_space_template' &&
                    !decisionSpaceTemplateManagement
                  ) {
                    return null
                  }

                  return (
                    <NavVertical.Item
                      key={item.path}
                      active={!!active}
                      as={Link}
                      leadingIcon={item.icon}
                      to={generatePath(item.path, urlParams)}
                      trailingAddon={
                        item.arrow && (
                          <SvgIcon
                            className="rtl:-scale-x-100"
                            name="chevron-right"
                          />
                        )
                      }
                    >
                      {item.title}
                    </NavVertical.Item>
                  )
                })}
          </div>
        </Suspense>
      </NavVertical.Group>
    </NavVertical>
  )
}
